import { useState } from 'react';

import Box, { type BoxProps } from '@liquidweb-dev/undercurrent/Box';
import Button from '@liquidweb-dev/undercurrent/Button';
import Collapse from '@liquidweb-dev/undercurrent/Collapse';
import Dialog, { type DialogProps } from '@liquidweb-dev/undercurrent/Dialog';
import DialogContent from '@liquidweb-dev/undercurrent/DialogContent';
import DialogTitle from '@liquidweb-dev/undercurrent/DialogTitle';
import PlusIcon from '@liquidweb-dev/undercurrent/icons/lucide/Plus';
import List from '@liquidweb-dev/undercurrent/List';
import Stack from '@liquidweb-dev/undercurrent/Stack';
import Tooltip from '@liquidweb-dev/undercurrent/Tooltip';
import Typography from '@liquidweb-dev/undercurrent/Typography';
import combineSx from '@liquidweb-dev/undercurrent/utility/combineSx';
import type { LWApiPublicSecureDataShareListResultRawI } from '@liquidweb-dev/club-sauce/public/secureDataShare/index.raw';
import useSecureDataShareList from 'modules/queries/secureDataShare/useList';
import { TransitionGroup } from 'react-transition-group';
import dialogSecureNotesContext, {
	useDialogSecureNotesContext,
} from './context';
import CreateNoteForm from './CreateNoteForm';
import SecureNoteRow from './SecureNoteRow';
import { Empty, ErrorState, Loading } from './States';

const Fade = ({ in: inProp, sx, ...rest }: BoxProps & { in: boolean }) => (
	<Box
		sx={combineSx(
			{
				position: 'absolute',
				top: 0,
				left: 0,
				right: 0,
				bottom: 0,
				opacity: inProp ? 1 : 0,
				transition: 'opacity 0.2s ease-in-out',
			},
			sx,
		)}
		{...rest}
	/>
);

interface SecureDataSharesProps {
	items: LWApiPublicSecureDataShareListResultRawI['items'];
	isLoading: boolean;
	isError: boolean;
	onRetry: () => void;
}

const SecureDataShares = ({
	items,
	isLoading,
	isError,
	onRetry,
}: SecureDataSharesProps) => {
	const [isViewingForm, setIsViewingForm] = useState(false);
	const { unlockedId } = useDialogSecureNotesContext();

	if (isError) return <ErrorState onRetry={onRetry} />;

	const isEmpty = !items.length;
	const showEmpty = isEmpty && !isViewingForm && !isLoading;

	return (
		<Stack sx={{ height: '100%', position: 'relative' }}>
			<Fade in={isLoading}>
				<Loading />
			</Fade>
			<Fade in={showEmpty}>
				<Empty
					onNewNoteClick={() => {
						setIsViewingForm(true);
					}}
				/>
			</Fade>

			<Stack sx={{ gap: 150 }}>
				<Tooltip
					title={
						unlockedId
							? 'You’re currently viewing a Secure Note. Lock it to continue.'
							: false
					}
				>
					<Button
						variant="secondary"
						onClick={() => {
							setIsViewingForm(true);
						}}
						startIcon={<PlusIcon />}
						sx={{ alignSelf: { md: 'start' } }}
						disabled={isViewingForm || Boolean(unlockedId)}
					>
						Create note
					</Button>
				</Tooltip>
				<List
					sx={{
						border: items.length > 0 ? '1px solid' : 'none',
						borderColor: ({ vars }) => vars.palette.uc.border.weak,
						minHeight: 0,
						maxHeight: 'none',
					}}
				>
					<Collapse in={isViewingForm}>
						<CreateNoteForm
							onSuccess={() => setIsViewingForm(false)}
							onCancel={() => setIsViewingForm(false)}
						/>
					</Collapse>
					<TransitionGroup>
						{items.map((item) => (
							<Collapse key={item.id}>
								<SecureNoteRow {...item} />
							</Collapse>
						))}
					</TransitionGroup>
				</List>
			</Stack>
		</Stack>
	);
};

export interface DialogSecureNotesProps extends Omit<DialogProps, 'onClose'> {
	onClose: () => void;
}

const DialogSecureNotes = ({
	open,
	onClose,
	...rest
}: DialogSecureNotesProps) => {
	const [unlockedId, setUnlockedId] = useState<number | null>(null);

	const {
		data: secureDataShare,
		isLoading,
		isError,
		refetch,
	} = useSecureDataShareList(
		{
			alsowith: ['ticketNumbers', 'added_date', 'description'],
			page_size: 999,
		},
		{ enabled: open },
	);

	const handleClose = () => {
		setUnlockedId(null);
		onClose();
	};

	return (
		<Dialog
			fullScreen
			onClose={handleClose}
			open={open}
			useSwipeableDrawer={false}
			{...rest}
		>
			<DialogTitle onClose={handleClose}>
				<Stack sx={{ gap: 50 }}>
					<div>Secure notes</div>
					<Typography
						variant="body-sm"
						sx={{ color: ({ vars }) => vars.palette.uc.text.medium }}
					>
						A safe way to pass information between you and support
					</Typography>
				</Stack>
			</DialogTitle>

			<DialogContent>
				<dialogSecureNotesContext.Provider
					value={{ closeDialog: handleClose, unlockedId, setUnlockedId }}
				>
					<SecureDataShares
						items={secureDataShare?.items || []}
						isLoading={isLoading}
						isError={isError}
						onRetry={refetch}
					/>
				</dialogSecureNotesContext.Provider>
			</DialogContent>
		</Dialog>
	);
};

export default DialogSecureNotes;
